<template>
  <div class="flex items-center justify-center gap-2" @paste="onPaste">
    <input
      v-for="(_, index) in length"
      :key="index"
      :ref="(el) => setInputRef(el, index)"
      :value="digits[index]"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      class="input w-12 h-12 px-0 text-center text-lg font-semibold"
      :disabled="disabled"
      :aria-label="t('auth.otpDigit')"
      @input="onInput(index, $event)"
      @keydown="onKeydown(index, $event)"
      @focus="onFocus(index)"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
  length: { type: Number, default: 6 },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'complete']);

const { t } = useI18n();

const length = computed(() => (Number.isFinite(props.length) && props.length > 0 ? props.length : 6));
const digits = ref(Array.from({ length: length.value }, () => ''));
const inputRefs = ref([]);

const emitValue = () => {
  const value = digits.value.join('');
  emit('update:modelValue', value);
  if (value.length === length.value && /^\d+$/.test(value)) {
    emit('complete', value);
  }
};

const normalizeDigitsFromValue = (value) => {
  const normalized = String(value || '').replace(/\D/g, '').slice(0, length.value);
  const next = Array.from({ length: length.value }, (_, index) => normalized[index] || '');
  digits.value = next;
};

watch(length, () => normalizeDigitsFromValue(props.modelValue), { immediate: true });
watch(() => props.modelValue, (value) => normalizeDigitsFromValue(value));

const focusIndex = async (index) => {
  await nextTick();
  const el = inputRefs.value[index];
  if (el && typeof el.focus === 'function') {
    el.focus();
    if (typeof el.select === 'function') el.select();
  }
};

const setInputRef = (el, index) => {
  if (!el) return;
  inputRefs.value[index] = el;
};

const setDigit = (index, value) => {
  const digit = String(value || '').replace(/\D/g, '').slice(-1);
  digits.value[index] = digit;
  emitValue();
};

const onInput = (index, event) => {
  const value = event?.target?.value ?? '';
  const digit = String(value).replace(/\D/g, '');

  if (!digit) {
    setDigit(index, '');
    return;
  }

  setDigit(index, digit);
  if (index < length.value - 1) {
    focusIndex(index + 1);
  }
};

const onFocus = (index) => {
  const el = inputRefs.value[index];
  if (el && typeof el.select === 'function') {
    el.select();
  }
};

const onKeydown = (index, event) => {
  if (!event) return;

  const key = event.key;
  if (key === 'Backspace') {
    if (digits.value[index]) {
      setDigit(index, '');
      return;
    }

    if (index > 0) {
      setDigit(index - 1, '');
      focusIndex(index - 1);
    }
    event.preventDefault();
    return;
  }

  if (key === 'ArrowLeft') {
    if (index > 0) focusIndex(index - 1);
    event.preventDefault();
    return;
  }

  if (key === 'ArrowRight') {
    if (index < length.value - 1) focusIndex(index + 1);
    event.preventDefault();
    return;
  }

  if (key === ' ' || key === 'Spacebar') {
    event.preventDefault();
  }
};

const onPaste = (event) => {
  const text = event?.clipboardData?.getData?.('text') ?? '';
  const normalized = String(text).replace(/\D/g, '').slice(0, length.value);
  if (!normalized) return;

  normalizeDigitsFromValue(normalized);
  emitValue();

  const nextIndex = Math.min(normalized.length, length.value - 1);
  focusIndex(nextIndex);
  event.preventDefault();
};
</script>
